import styled, {css} from 'styled-components';
import {goalListStatusBarColors} from '@ui/style/goal-status';
import {getColorFromTheme} from '@ui/ui-utils';
import {GoalAttentionStatus} from '@utils/goal-status';
import {
  SortMenu,
  SortOption,
  Toolbar,
} from '../my-objective/my-goals-v2.styles';

// Flat Team Goals styles follow Beam's Graphik typography and semantic status palette.

export const Wrapper = styled.section<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$compact}) => ($compact ? '0' : '24px')};
`;

export const FilterWrapper = styled.div`
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};

  .pd {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;

    > :first-child {
      min-width: 0;
      flex: 1;
    }
  }

  .s-b-wrapper {
    padding: 16px 24px;
    border-top: 1px solid ${getColorFromTheme('borderLight')};
  }

  @media (max-width: 760px) {
    .pd {
      flex-direction: column;
      align-items: stretch;
      padding: 16px;
    }

    .s-b-wrapper {
      padding: 16px;
    }
  }
`;

export const GroupBy = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

export const GroupByButton = styled.button<{$open: boolean}>`
  display: flex;
  min-width: 148px;
  height: 40px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid
    ${({$open, theme}) =>
      $open ? theme.colors.purple300 : theme.colors.borderDark};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font: 600 13px/20px Graphik, sans-serif;
  white-space: nowrap;

  strong {
    color: ${getColorFromTheme('purple300')};
    font-weight: 600;
  }

  svg {
    flex: 0 0 16px;
    transition: transform 160ms ease;
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 2px;
  }
`;

export const TeamToolbar = styled(Toolbar)<{$withSearch?: boolean}>`
  > div:last-child {
    width: auto;
  }

  ${({$withSearch}) =>
    $withSearch &&
    css`
      > div {
        flex-wrap: nowrap;
      }

      > div:first-child {
        min-width: 0;
        flex: 1 1 auto;
      }

      > div:first-child > label {
        min-width: 160px;
        max-width: 240px;
        flex: 1 1 240px;
      }

      > div:first-child > div {
        flex: 0 0 auto;
      }

      > div:last-child {
        flex: 0 0 auto;
      }
    `}

  @media (min-width: 781px) and (max-width: 1100px) {
    > div:first-child > label {
      min-width: 120px;
      max-width: 180px;
      flex-basis: 180px;
    }

    .toolbar-control-prefix,
    ${GroupByButton} > span:first-child {
      display: none;
    }

    ${GroupByButton} {
      min-width: auto;
    }
  }

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: stretch;

    > div:last-child {
      width: 100%;
      justify-content: flex-start;
    }

    > div {
      flex-wrap: wrap;
    }
  }
`;

export const ToolbarSearch = styled.label`
  display: flex;
  width: 240px;
  height: 40px;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};

  &:focus-within {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: 0 0 0 2px ${getColorFromTheme('purple200')};
  }

  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${getColorFromTheme('blackTwo')};
    font: 400 13px/20px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textMuted')};
    }

    &::-webkit-search-cancel-button {
      display: none;
      appearance: none;
    }
  }

  button {
    display: grid;
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    place-items: center;
    border-radius: 4px;
    color: ${getColorFromTheme('textBody')};

    &:hover,
    &:focus-visible {
      outline: none;
      background: ${getColorFromTheme('purple100')};
      color: ${getColorFromTheme('purple300')};
    }
  }

  @media (max-width: 780px) {
    width: min(100%, 320px);
  }
`;

export const FilterOptionSearch = styled(ToolbarSearch)`
  width: 100%;
  height: 36px;
  margin-bottom: 8px;
  padding: 6px 8px;
`;

export const EmptyFilterOptions = styled.p`
  margin: 12px 0 6px;
  color: ${getColorFromTheme('textMuted')};
  font: 400 13px/20px Graphik, sans-serif;
`;

export const GroupByMenu = styled(SortMenu)`
  right: 0;
  left: auto;
`;

export const GroupByOption = styled(SortOption)<{$active: boolean}>``;

export const PeopleList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PersonSection = styled.section`
  padding: 0 0 14px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  & + & {
    padding-top: 14px;
  }

  &:last-child {
    border-bottom: 0;
  }
`;

export const PersonHeader = styled.div`
  display: grid;
  min-height: 52px;
  grid-template-columns: minmax(220px, 280px) minmax(270px, 1fr) auto;
  align-items: center;
  gap: 20px;

  @media (max-width: 820px) {
    grid-template-columns: minmax(250px, 1fr) auto;

    > .person-summary {
      grid-column: 1 / -1;
      grid-row: 2;
    }
  }

  @media (max-width: 680px) {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    > .person-summary {
      width: 100%;
      padding-left: 0;
    }
  }
`;

const personIdentityStyles = css`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
`;

export const PersonIdentity = styled.div`
  ${personIdentityStyles}
`;

export const PersonIdentityButton = styled.button`
  ${personIdentityStyles}
  width: fit-content;
  max-width: 100%;
  padding: 4px;
  margin: -4px;
  border-radius: 8px;
  text-align: left;

  &:hover {
    background: ${getColorFromTheme('backgroundDark')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 2px;
  }
`;

export const PersonChevron = styled.span<{$expanded: boolean}>`
  display: grid;
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  place-items: center;
  margin-left: 2px;
  color: ${getColorFromTheme('textMuted')};

  svg {
    transition: transform 160ms ease;
    transform: rotate(${({$expanded}) => ($expanded ? '0deg' : '-90deg')});
  }
`;

export const PersonName = styled.div`
  min-width: 0;

  h2,
  p {
    overflow: hidden;
    margin: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  h2 {
    color: ${getColorFromTheme('blackTwo')};
    font: 600 14px/20px Graphik, sans-serif;
  }

  p {
    color: ${getColorFromTheme('textBody')};
    font: 400 13px/20px Graphik, sans-serif;
  }
`;

export const PersonSummary = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 360px) max-content;
  align-items: center;
  justify-content: start;
  gap: 16px;
`;

export const SegmentedTrack = styled.div`
  display: flex;
  width: 100%;
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: ${getColorFromTheme('borderLight')};
`;

export const StatusSegment = styled.span<{
  $performance: GoalAttentionStatus;
  $width: number;
}>`
  width: ${({$width}) => $width}%;
  height: 100%;
  flex: 0 0 auto;
  background: ${({$performance}) => goalListStatusBarColors[$performance]};
`;

export const AttentionSummary = styled.span`
  display: flex;
  min-width: 126px;
  align-items: center;
  gap: 6px;
  color: ${getColorFromTheme('textBody')};
  font: 400 13px/20px Graphik, sans-serif;
  white-space: nowrap;

  span {
    display: inline-flex;
    align-items: center;
  }

  strong {
    margin-right: 4px;
    color: ${getColorFromTheme('textDark')};
    font-weight: 600;
  }

  i {
    margin-right: 6px;
    color: ${getColorFromTheme('textMuted')};
    font-style: normal;
  }
`;

export const PersonOutcome = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;
  white-space: nowrap;

  @media (max-width: 680px) {
    width: 100%;
  }

  span {
    color: ${getColorFromTheme('textBody')};
    font: 400 12px/18px Graphik, sans-serif;
  }

  strong {
    color: ${getColorFromTheme('textBody')};
    font: 600 16px/24px Graphik, sans-serif;
  }
`;

export const NoGoalsPill = styled.span`
  padding: 6px 12px;
  border-radius: 10px;
  background: ${getColorFromTheme('backgroundDark')};
  color: #d52a2a;
  font: 500 13px/20px Graphik, sans-serif;
`;

export const PersonCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

export const NoGoalsActions = styled.div`
  display: flex;
  min-width: 0;
  grid-column: 3;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  white-space: nowrap;

  button {
    padding: 2px 0 2px 16px;
    border-left: 1px solid ${getColorFromTheme('borderLight')};
    background: transparent;
    color: ${getColorFromTheme('purple300')};
    font: 500 13px/20px Graphik, sans-serif;

    &:hover,
    &:focus-visible {
      outline: none;
      text-decoration: underline;
    }
  }

  @media (max-width: 820px) {
    grid-column: 2;
  }
`;

export const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const StatusCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StackedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const StatusSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 24px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

export const StatusHeading = styled.h2<{
  $performance: GoalAttentionStatus;
}>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font: 600 16px/24px Graphik, sans-serif;

  &::before {
    width: 10px;
    height: 10px;
    flex: 0 0 10px;
    border-radius: 50%;
    background: ${({$performance}) => goalListStatusBarColors[$performance]};
    content: '';
  }
`;

const panel = css`
  display: grid;
  min-height: 260px;
  place-items: center;
  padding: 32px;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;
  text-align: center;
`;

export const EmptyPanel = styled.div`
  ${panel}

  div {
    max-width: 440px;
  }

  h2 {
    margin: 0 0 4px;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 16px/24px Graphik, sans-serif;
  }

  p {
    margin: 0 0 14px;
  }

  button {
    height: 38px;
    padding: 8px 16px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 8px;
    color: ${getColorFromTheme('blackTwo')};
    font: 600 12px/20px Graphik, sans-serif;
  }
`;

export const LoadingPanel = styled.div`
  ${panel}
`;
